import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";

const TABS = [
  { key: "business",    label: "Business Types" },
  { key: "job",         label: "Job Trades" },
  { key: "event",       label: "Events" },
  { key: "news",        label: "News" },
  { key: "deal",        label: "Deals" },
  { key: "free_item",   label: "Market" },
  { key: "order_food",  label: "Order Food" },
  { key: "order_retail", label: "Order Shops" },
];

const ORDER_TABS = new Set(["order_food", "order_retail"]);

interface CategoryItem { id: number; name: string; sortOrder: number; }

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function AdminCategories() {
  const [tab, setTab] = useState("business");
  const [data, setData] = useState<Record<string, CategoryItem[]>>({});
  const [orderCats, setOrderCats] = useState<{ food: CategoryItem[]; retail: CategoryItem[] }>({ food: [], retail: [] });
  const [savedOrderCats, setSavedOrderCats] = useState<{ food: CategoryItem[]; retail: CategoryItem[] }>({ food: [], retail: [] });
  const [savingOrder, setSavingOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const { toast } = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, orderRes] = await Promise.all([
        fetch("/api/admin/categories", { headers: authHeaders() }),
        fetch("/api/admin/order-categories", { headers: authHeaders() }),
      ]);
      if (!catRes.ok || !orderRes.ok) throw new Error("Categories unavailable");
      const catJson = await catRes.json() as { categories: Record<string, CategoryItem[]> };
      setData(catJson.categories ?? {});
      const orderJson = await orderRes.json() as { items: (CategoryItem & { type: string })[] };
      const items = orderJson.items ?? [];
      const grouped = {
        food: items.filter(c => c.type === "food"),
        retail: items.filter(c => c.type === "retail"),
      };
      setOrderCats(grouped);
      setSavedOrderCats(grouped);
    } catch {
      toast({ title: "Failed to load categories", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      if (ORDER_TABS.has(tab)) {
        const type = tab === "order_food" ? "food" : "retail";
        const res = await fetch("/api/admin/order-categories", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ name: newName.trim(), type }),
        });
        if (!res.ok) throw new Error();
      } else {
        const res = await fetch("/api/admin/categories", {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ type: tab, name: newName.trim() }),
        });
        if (!res.ok) throw new Error();
      }
      setNewName("");
      await fetchAll();
      toast({ title: "Category added" });
    } catch {
      toast({ title: "Failed to add category", variant: "destructive" });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    try {
      if (ORDER_TABS.has(tab)) {
        const res = await fetch(`/api/admin/order-categories/${id}`, { method: "DELETE", headers: authHeaders() });
        if (!res.ok) throw new Error("Delete failed");
      } else {
        const res = await fetch(`/api/admin/categories/${id}`, { method: "DELETE", headers: authHeaders() });
        if (!res.ok) throw new Error("Delete failed");
      }
      await fetchAll();
      toast({ title: `"${name}" removed` });
    } catch {
      toast({ title: "Failed to delete category", variant: "destructive" });
    }
  };

  const currentItems = ORDER_TABS.has(tab)
    ? (tab === "order_food" ? orderCats.food : orderCats.retail)
    : (data[tab] ?? []);
  const currentTab = TABS.find(t => t.key === tab);
  const orderType = tab === "order_food" ? "food" : "retail";
  const canReorder = ORDER_TABS.has(tab);
  const orderDirty = canReorder && orderCats[orderType].some((cat, index) => cat.id !== savedOrderCats[orderType][index]?.id);

  function moveCategory(index: number, direction: number) {
    if (savingOrder || adding || loading) return;
    setOrderCats(previous => {
      const items = [...previous[orderType]];
      const next = index + direction;
      if (next < 0 || next >= items.length) return previous;
      [items[index], items[next]] = [items[next], items[index]];
      return { ...previous, [orderType]: items };
    });
  }

  async function saveOrder() {
    if (!orderDirty || savingOrder) return;
    setSavingOrder(true);
    try {
      const res = await fetch("/api/admin/order-categories/reorder", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ type: orderType, orderedIds: orderCats[orderType].map(c => c.id), expectedOrderIds: savedOrderCats[orderType].map(c => c.id) }),
      });
      const result = await res.json() as { items?: CategoryItem[]; error?: string };
      if (!res.ok || !result.items) throw new Error(result.error ?? "Could not save the category order.");
      const items = result.items;
      setOrderCats(previous => ({ ...previous, [orderType]: items }));
      setSavedOrderCats(previous => ({ ...previous, [orderType]: items }));
      toast({ title: "Customer category order saved" });
    } catch (error) {
      toast({ title: error instanceof Error ? error.message : "Could not save the category order.", variant: "destructive" });
    } finally { setSavingOrder(false); }
  }

  return (
    <AdminLayout current="/admin/categories">
      <div>
        <h1 className="text-2xl font-bold text-primary mb-1">Categories &amp; Trades</h1>
        <p className="text-sm text-slate-500 mb-6">
          Manage the filter options that appear across the app — businesses, jobs, events, deals, free items, news and order categories.
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map(t => {
            const count = ORDER_TABS.has(t.key)
              ? (t.key === "order_food" ? orderCats.food.length : orderCats.retail.length)
              : (data[t.key]?.length ?? 0);
            return (
              <button
                key={t.key}
                disabled={savingOrder || adding || (orderDirty && t.key !== tab)}
                onClick={() => { setTab(t.key); setNewName(""); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  tab === t.key
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
                }`}
              >
                {t.label}
                <span className={`ml-2 text-xs rounded-full px-1.5 py-0.5 ${tab === t.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="text-base font-semibold text-slate-800 mb-4">{currentTab?.label}</h2>
          {canReorder && <div className="mb-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-800">Customer display order</p>
            <p className="text-sm text-slate-500 mt-1">Move categories up or down, then save. Customers see this order on the website and app.</p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button onClick={saveOrder} disabled={!orderDirty || savingOrder || loading || adding}>{savingOrder ? "Saving…" : "Save order"}</Button>
              <Button variant="outline" disabled={!orderDirty || savingOrder} onClick={() => setOrderCats(previous => ({ ...previous, [orderType]: savedOrderCats[orderType] }))}>Reset</Button>
              {orderDirty && <span role="status" className="text-xs text-amber-700">Unsaved order — save or reset before leaving this tab or adding/removing categories.</span>}
            </div>
            <button type="button" disabled={savingOrder || adding || loading} onClick={() => void fetchAll()} className="text-xs font-semibold text-primary underline mt-3">{orderDirty ? "Refresh and discard changes" : "Refresh categories"}</button>
          </div>}

          <div className="flex gap-2 mb-6">
            <Input
              placeholder={`New ${currentTab?.label.toLowerCase().replace(/s$/, "")} name…`}
              value={newName}
              disabled={savingOrder || orderDirty}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
              className="max-w-xs"
            />
            <Button
              onClick={handleAdd}
              disabled={loading || adding || savingOrder || orderDirty || !newName.trim()}
              className="bg-primary hover:bg-primary/90 gap-1"
            >
              <Plus className="w-4 h-4" />
              Add
            </Button>
          </div>

          {loading ? (
            <div className="flex flex-col gap-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : currentItems.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No categories yet — add one above.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {currentItems.map((cat, index) => (
                <div
                  key={cat.id}
                  className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg group"
                >
                  <span className="text-sm font-medium text-slate-700 min-w-0 break-words">{canReorder && <span className="text-slate-400 mr-2">{index + 1}.</span>}{cat.name}</span>
                  <div className="flex items-center shrink-0 ml-2">
                  {canReorder && <>
                    <button type="button" aria-label={`Move ${cat.name} up`} disabled={index === 0 || savingOrder || adding} onClick={() => moveCategory(index, -1)} className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-slate-700 hover:bg-slate-200 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                    <button type="button" aria-label={`Move ${cat.name} down`} disabled={index === currentItems.length - 1 || savingOrder || adding} onClick={() => moveCategory(index, 1)} className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-slate-700 hover:bg-slate-200 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                  </>}
                  <button
                    disabled={savingOrder || adding || orderDirty}
                    onClick={() => handleDelete(cat.id, cat.name)}
                    className="text-red-400 hover:text-red-600 transition-colors p-1 rounded"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
